import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { Portuguese } from "flatpickr/dist/l10n/pt";
import ApiService from "../util/ApiService";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CustomPagination from "../util/CustomPagination";
import DropdownPortal from "../util/DropdownPortal";
import VideoCallDetailModal from "./VideoCallDetailModal";

require("./assets/css/Video.css");

const VideoHistoryPage = () => {
    document.title = "Histórico de Atendimentos | Plantão Rápido";

    const buttonRefs = useRef({});
    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const [attendances, setAttendances] = useState([]);
    const [selectedAttendance, setSelectedAttendance] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [departments, setDepartments] = useState([]);

    const [filters, setFilters] = useState({
        status: { value: "completed", label: "Finalizado" },
        department_id: null,
        date_from: null,
        date_to: null
    });

    const statusOptions = [
        { value: "completed", label: "Finalizado" },
        { value: "cancelled", label: "Cancelado" },
        { value: "no_show", label: "Não Compareceu" },
        { value: "", label: "Todos" }
    ];

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 15,
        total: 0,
        lastPage: 0
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchAttendances();
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/departments/list-department`,
                { per_page: 100, status: 1 },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setDepartments(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Erro ao carregar departamentos:', error);
        }
    };

    const fetchAttendances = async (page = 1) => {
        try {
            setLoading(true);

            const params = {
                page: page,
                per_page: pagination.pageSize
            };

            // Filtrar por status finalizados (completed, cancelled, no_show)
            if (filters?.status?.value) {
                params.status = filters.status.value;
            } else {
                // Se não tiver filtro, buscar apenas finalizados
                params.status = 'completed,cancelled,no_show';
            }

            if (filters?.department_id?.value) {
                params.department_id = filters.department_id.value;
            }

            if (filters?.date_from) {
                params.date_from = filters.date_from;
            }

            if (filters?.date_to) {
                params.date_to = filters.date_to;
            }

            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/attendances`,
                params,
                true
            );

            const { data, pagination: pagInfo } = ApiService.handleApiResponse(response);
            setAttendances(data || []);
            setPagination(pagInfo || { current: 1, pageSize: 15, total: 0 });
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        fetchAttendances(page);
    };

    const handleClearFilters = () => {
        setFilters({
            status: { value: "completed", label: "Finalizado" },
            department_id: null,
            date_from: null,
            date_to: null
        });
    };

    const handleViewDetail = (attendance) => {
        setSelectedAttendance(attendance);
        setShowDetailModal(true);
    };

    const getStatusBadge = (status) => {
        const badges = {
            waiting: { class: 'bg-secondary', icon: 'fa-hourglass', text: 'Aguardando' },
            in_queue: { class: 'bg-warning', icon: 'fa-clock', text: 'Na Fila' },
            in_progress: { class: 'bg-success', icon: 'fa-video-camera', text: 'Em Atendimento' },
            completed: { class: 'bg-info', icon: 'fa-check', text: 'Finalizado' },
            cancelled: { class: 'bg-danger', icon: 'fa-times', text: 'Cancelado' },
            no_show: { class: 'bg-dark', icon: 'fa-user-times', text: 'Não Compareceu' }
        };

        const badge = badges[status] || badges.completed;

        return (
            <span className={`badge ${badge.class} d-inline-flex align-items-center`}>
                <i className={`fa ${badge.icon} me-1`}></i>
                {badge.text}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const calculateDuration = (startedAt, endedAt) => {
        if (!startedAt || !endedAt) return '-';

        const start = new Date(startedAt);
        const end = new Date(endedAt);
        const diff = Math.floor((end - start) / 1000);

        const hours = Math.floor(diff / 3600);
        const minutes = Math.floor((diff % 3600) / 60);

        if (hours > 0) {
            return `${hours}h ${minutes}min`;
        }
        return `${minutes}min`;
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Histórico" pageTitle="Vídeo Chamada" />

                    {/* Stats Cards */}
                    <Row className="mb-4">
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-info">
                                    {attendances.filter(a => a.status === 'completed').length}
                                </div>
                                <div className="stat-label">Finalizados</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-danger">
                                    {attendances.filter(a => a.status === 'cancelled').length}
                                </div>
                                <div className="stat-label">Cancelados</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value text-dark">
                                    {attendances.filter(a => a.status === 'no_show').length}
                                </div>
                                <div className="stat-label">Não Compareceram</div>
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="video-stats-card">
                                <div className="stat-value">{pagination.total}</div>
                                <div className="stat-label">Total</div>
                            </div>
                        </Col>
                    </Row>

                    {/* Filters */}
                    <Row className="mb-3">
                        <Col md={3}>
                            <div className="form-group">
                                <label>Status</label>
                                <Select
                                    options={statusOptions}
                                    value={filters.status}
                                    onChange={(option) => setFilters(prev => ({ ...prev, status: option }))}
                                    placeholder="Selecione o status"
                                    isClearable
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="form-group">
                                <label>Departamento</label>
                                <Select
                                    options={departments.map(d => ({ value: d.id, label: d.name }))}
                                    value={filters.department_id}
                                    onChange={(option) => setFilters(prev => ({ ...prev, department_id: option }))}
                                    placeholder="Selecione"
                                    isClearable
                                />
                            </div>
                        </Col>
                        <Col md={2}>
                            <div className="form-group">
                                <label>Data Início</label>
                                <Flatpickr
                                    className="form-control"
                                    value={filters.date_from}
                                    onChange={([date]) => setFilters(prev => ({
                                        ...prev,
                                        date_from: date ? date.toISOString().split('T')[0] : null
                                    }))}
                                    options={{
                                        dateFormat: "d/m/Y",
                                        locale: Portuguese
                                    }}
                                    placeholder="De"
                                />
                            </div>
                        </Col>
                        <Col md={2}>
                            <div className="form-group">
                                <label>Data Fim</label>
                                <Flatpickr
                                    className="form-control"
                                    value={filters.date_to}
                                    onChange={([date]) => setFilters(prev => ({
                                        ...prev,
                                        date_to: date ? date.toISOString().split('T')[0] : null
                                    }))}
                                    options={{
                                        dateFormat: "d/m/Y",
                                        locale: Portuguese
                                    }}
                                    placeholder="Até"
                                />
                            </div>
                        </Col>
                        <Col md={2} className="d-flex align-items-end">
                            <button
                                className="btn me-2"
                                onClick={handleClearFilters}
                            >
                                <i className="fa fa-eraser me-1"></i> Limpar
                            </button>
                            <button
                                className="btn btn-success"
                                onClick={() => fetchAttendances(1)}
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    <><i className="fa fa-search me-1"></i> Buscar</>
                                )}
                            </button>
                        </Col>
                    </Row>

                    {/* History List */}
                    <div className="list-container">
                        {loading ? (
                            <Skeleton count={5} height={100} />
                        ) : attendances.length > 0 ? (
                            attendances.map((attendance) => (
                                <Col lg={12} key={attendance.id}>
                                    <Card className="mb-2 p-2 shadow-sm">
                                        <CardBody className="d-flex justify-content-between align-items-center p-3">
                                            <div className="d-flex align-items-center">
                                                <div className="video-user-avatar me-3">
                                                    {attendance.client?.name?.charAt(0)?.toUpperCase() || 'C'}
                                                </div>
                                                <div>
                                                    <h5 className="mb-1">{attendance.client?.name || 'Cliente'}</h5>
                                                    <p className="text-muted mb-1">
                                                        <i className="fa fa-building me-1"></i>
                                                        {attendance.department?.name || 'Sem departamento'}
                                                    </p>
                                                    <p className="text-muted mb-0 small">
                                                        <i className="fa fa-ticket me-1"></i>
                                                        {attendance.protocol}
                                                        <span className="ms-3">
                                                            <i className="fa fa-clock me-1"></i>
                                                            {formatDate(attendance.created_at)}
                                                        </span>
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="text-center">
                                                {getStatusBadge(attendance.status)}
                                                <p className="text-muted mb-0 mt-2 small">
                                                    <i className="fa fa-stopwatch me-1"></i>
                                                    Duração: {calculateDuration(attendance.started_at, attendance.completed_at)}
                                                </p>
                                                {attendance.professional && (
                                                    <p className="text-muted mb-0 small">
                                                        <i className="fa fa-user me-1"></i>
                                                        {attendance.professional.name}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="dropdown">
                                                <button
                                                    className="btn btn-outline-primary btn-sm"
                                                    ref={(el) => (buttonRefs.current[attendance.id] = el)}
                                                    onClick={() => setDropdownOpenId(dropdownOpenId === attendance.id ? null : attendance.id)}
                                                >
                                                    <i className="fa fa-ellipsis-v"></i> Ações
                                                </button>

                                                <DropdownPortal
                                                    parentRef={{ current: buttonRefs.current[attendance.id] }}
                                                    show={dropdownOpenId === attendance.id}
                                                    onClose={() => setDropdownOpenId(null)}
                                                >
                                                    <ul className="dropdown-menu show">
                                                        <li>
                                                            <button
                                                                className="dropdown-item"
                                                                onClick={() => {
                                                                    handleViewDetail(attendance);
                                                                    setDropdownOpenId(null);
                                                                }}
                                                            >
                                                                <i className="fa fa-eye me-2"></i> Ver Detalhes
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </DropdownPortal>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            ))
                        ) : (
                            <div className="video-empty-state">
                                <i className="fa fa-history"></i>
                                <h5 className="text-muted">Nenhum atendimento encontrado</h5>
                                <p className="text-muted">O histórico de atendimentos aparecerá aqui.</p>
                            </div>
                        )}
                    </div>

                    <CustomPagination
                        onChange={handlePageChange}
                        pagination={pagination}
                    />

                    {showDetailModal && (
                        <VideoCallDetailModal
                            show={showDetailModal}
                            onHide={() => {
                                setShowDetailModal(false);
                                setSelectedAttendance(null);
                            }}
                            call={selectedAttendance}
                        />
                    )}
                </Container>
            </div>
        </React.Fragment>
    );
};

export default VideoHistoryPage;
